import React, { useState, useEffect, useRef } from 'react';
import './ChatWidget.css';

// Configuration
const CONFIG = {
  API_URL: 'https://api.solidrust.ai/v1/chat/completions',
  MODEL: 'vllm-primary',
  MAX_TOKENS: 512,
  TEMPERATURE: 0.7,
  // TechFusion demo key (Base64 encoded) - dedicated key for techfusion.ca
  // Security enforced by PAM validation, rate limiting, CORS whitelist
  // Stripe customer: cus_Tq3m1H5Xr841H0
  DEFAULT_API_KEY_ENCODED: 'c3J0X3Byb2RfYzNhMDRhNDc4MTY1ZTc2NWNlOGZlY2VmZDRiYTJlOWM5MTgyMzA2NGJiYTM3OTIxNzM1ODg3NmIwYmQ1YWUyOQ==',
  STORAGE_KEY: 'techfusion_chat_history',
  MAX_HISTORY: 20
};

// TechFusion AI/ML consultancy assistant prompt
const SYSTEM_PROMPT = `You are the TechFusion AI Assistant, representing TechFusion - an AI/ML consultancy that builds intelligent systems.

About TechFusion:
- AI/ML consultancy specializing in production AI solutions
- A division of SolidRusT Networks (owns the infrastructure)
- Built SolidRusT.AI Platform, MyAshes.ai, and AIdiant.com
- Services: Custom model development, MLOps, AI architecture, integrations

Our Services:
1. AI/ML Engineering - Custom models, fine-tuning, LLMs, computer vision
2. MLOps & Infrastructure - Production pipelines, GPU orchestration, Kubernetes
3. Architecture & Design - RAG systems, agent frameworks, hybrid cloud
4. AI Integration - APIs, chat interfaces, intelligent automation

Case Studies:
- SolidRusT.AI: Complete AI inference platform with vLLM, LiteLLM, failover
- MyAshes.ai: AI gaming companion with real-time knowledge and RAG
- AIdiant.com: Per-member AI assistants with tool-calling and embeddings

Your role:
- Help visitors understand our AI/ML consulting services
- Answer questions about our capabilities and case studies
- Guide potential clients toward starting a project
- Be professional, knowledgeable, and concise
- If interested, direct them to the contact form

Keep responses brief (2-3 sentences) unless more detail is requested.`;

// Quick suggestion prompts
const SUGGESTIONS = [
  'What services do you offer?',
  'Tell me about your AI platform',
  'RAG & embeddings?',
  'Start a project'
];

function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Get API key (user override or default)
  const getApiKey = () => {
    const userKey = localStorage.getItem('srt_api_key');
    if (userKey) return userKey;

    try {
      return atob(CONFIG.DEFAULT_API_KEY_ENCODED);
    } catch {
      return null;
    }
  };

  // Load chat history on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CONFIG.STORAGE_KEY);
      if (saved) {
        const history = JSON.parse(saved);
        setMessages(history);
        if (history.length > 0) {
          setShowSuggestions(false);
        }
      } else {
        // Add initial greeting
        setMessages([{
          role: 'assistant',
          content: "Hi! I'm the TechFusion AI Assistant. I can help you learn about our AI/ML consulting services, case studies, and how we build intelligent systems. What would you like to know?",
          timestamp: Date.now()
        }]);
      }
    } catch (e) {
      setMessages([{
        role: 'assistant',
        content: "Hi! I'm the TechFusion AI Assistant. I can help you learn about our AI/ML consulting services, case studies, and how we build intelligent systems. What would you like to know?",
        timestamp: Date.now()
      }]);
    }
  }, []);

  // Save messages to localStorage when they change
  useEffect(() => {
    if (messages.length > 0) {
      try {
        const toSave = messages.slice(-CONFIG.MAX_HISTORY);
        localStorage.setItem(CONFIG.STORAGE_KEY, JSON.stringify(toSave));
      } catch (e) {
        // localStorage might be full
      }
    }
  }, [messages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Call the AI API
  const callAPI = async (userMessage) => {
    const apiKey = getApiKey();
    if (!apiKey) {
      throw new Error('API key not configured. Please try again later.');
    }

    // Build messages array with history
    const apiMessages = [
      { role: 'system', content: SYSTEM_PROMPT },
      ...messages.slice(-10).map(m => ({
        role: m.role,
        content: m.content
      })),
      { role: 'user', content: userMessage }
    ];

    const response = await fetch(CONFIG.API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify({
        model: CONFIG.MODEL,
        messages: apiMessages,
        max_tokens: CONFIG.MAX_TOKENS,
        temperature: CONFIG.TEMPERATURE
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    const data = await response.json();
    return data.choices?.[0]?.message?.content || 'Sorry, I could not generate a response.';
  };

  // Send message
  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    // Add user message
    const userMsg = { role: 'user', content: text, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setShowSuggestions(false);
    setIsLoading(true);

    try {
      const response = await callAPI(text);
      const assistantMsg = { role: 'assistant', content: response, timestamp: Date.now() };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (error) {
      const errorMsg = { role: 'error', content: error.message, timestamp: Date.now() };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle keyboard events
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle suggestion click
  const handleSuggestion = (suggestion) => {
    setInputValue(suggestion);
    setTimeout(() => {
      handleSend();
    }, 100);
  };

  // Escape to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  return (
    <div className="chat-widget">
      {/* Toggle Button */}
      <button
        className={`chat-toggle ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close chat' : 'Open chat assistant'}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          className="chat-window"
          role="dialog"
          aria-label="Chat with TechFusion Assistant"
        >
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-info">
              <div className="chat-avatar">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
              </div>
              <div className="chat-header-text">
                <h3>TechFusion Assistant</h3>
                <span className="chat-status">
                  <span className="status-dot"></span>
                  Online
                </span>
              </div>
            </div>
            <button
              className="chat-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="chat-messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`chat-message ${msg.role}`}>
                <div className="message-avatar">
                  {msg.role === 'user' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  ) : msg.role === 'error' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                  )}
                </div>
                <div className="message-content">
                  {msg.content}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isLoading && (
              <div className="chat-message assistant">
                <div className="message-avatar">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                </div>
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions */}
          {showSuggestions && (
            <div className="chat-suggestions">
              {SUGGESTIONS.map((suggestion, idx) => (
                <button
                  key={idx}
                  className="suggestion-btn"
                  onClick={() => handleSuggestion(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="chat-input-area">
            <textarea
              ref={inputRef}
              className="chat-input"
              placeholder="Ask about our community..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading}
            />
            <button
              className="chat-send"
              onClick={handleSend}
              disabled={!inputValue.trim() || isLoading}
              aria-label="Send message"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </div>

          {/* Footer */}
          <div className="chat-footer">
            Powered by <a href="https://solidrust.ai" target="_blank" rel="noopener noreferrer">SolidRusT AI</a>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatWidget;
